import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { Modal } from "@material-ui/core";
import theme from "../../../assets/theme";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import { CustomInputField } from "../../common/FormInputs";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import { getErrorMessage } from "../../../helpers/functions";
import CaseService from "../../../services/CaseService";

export default function EditBundleName({
  editBundle,
  setEditBundle,
  id,
  caseId,
  title,
  setRefreshDetails,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const [bundle, setBundle] = useState(title);
  const [error, setError] = useState("");

  const editBundleName = async (value) => {
    try {
      const payload = { bundleName: value };
      setLoader({ state: true, message: `Update Bundle Name` });
      const res = await CaseService.editBundleName(payload, caseId, id);
      if (res?.success === false) {
        enqueueSnackbar(res.message, { variant: "error" });
        return;
      }
      if (res) {
        enqueueSnackbar("Bundle updated Successfully", { variant: "success" });
      }
      setRefreshDetails(true);
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
      setEditBundle(false);
    }
  };

  const handleChange = (event) => {
    const name = event.target.value;
    /* eslint-disable no-useless-escape */
    const regexPattern = /^[^\/\\=]+$/;
    if (regexPattern.test(name)) {
      setError(false);
    } else {
      setError(
        `Bundle name should not contain slashes ("/") and equal signs ("=")`,
      );
    }
    setBundle(name);
  };

  return (
    <Modal
      open={editBundle}
      onClose={() => setEditBundle(false)}
      disableRestoreFocus={true}
    >
      <Overlay>
        <ModalCard role="presentation">
          <ModalHeader>
            <HeaderText>
              <ModalTitle>Edit Bundle Name</ModalTitle>
              <ModalSubtitle>
                Update the name for this document bundle
              </ModalSubtitle>
            </HeaderText>
            <CloseBtn onClick={() => setEditBundle(false)} aria-label="Close">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </CloseBtn>
          </ModalHeader>

          <ModalDivider />

          <ModalBody>
            <CurrentName>
              <CurrentLabel>Current name</CurrentLabel>
              <CurrentValue>{title}</CurrentValue>
            </CurrentName>

            <FieldGroup>
              <FieldLabel>
                New Bundle Name <Required>*</Required>
              </FieldLabel>
              <CustomInputField
                variant="outlined"
                value={bundle}
                error={!!error}
                helperText={error}
                required={true}
                onChange={(e) => handleChange(e)}
              />
            </FieldGroup>

            {bundle && !error && bundle !== title && (
              <ChangePreview>
                <ChangeRow>
                  <ChangeFrom>{title}</ChangeFrom>
                  <ArrowIcon>→</ArrowIcon>
                  <ChangeTo>{bundle}</ChangeTo>
                </ChangeRow>
              </ChangePreview>
            )}
          </ModalBody>

          <ModalFooter>
            <PrimaryOutlinedCloseCTAButton
              style={{ flex: 1 }}
              onClick={() => setEditBundle(false)}
            >
              Cancel
            </PrimaryOutlinedCloseCTAButton>
            <PrimaryCTAButton
              style={{ flex: 1 }}
              disabled={!bundle || !!error}
              onClick={() => editBundleName(bundle)}
            >
              Save Changes
            </PrimaryCTAButton>
          </ModalFooter>
        </ModalCard>
      </Overlay>
    </Modal>
  );
}

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -48%) scale(0.97); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
`;

const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 32px);
  max-width: 480px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.18),
    0 8px 24px rgba(0, 131, 140, 0.1);
  animation: ${fadeSlideIn} 0.25s ease;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 24px 20px;
`;

const HeaderText = styled.div`
  flex: 1;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 18px;
  color: #293461;
  line-height: 1.2;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #ef4444;
  }
`;

const ModalDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  margin: 0 24px;
`;

const ModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CurrentName = styled.div`
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
`;

const CurrentLabel = styled.p`
  margin: 0 0 4px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const CurrentValue = styled.p`
  margin: 0;
  font-size: 14px;
  color: #475569;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: #334155;
`;

const Required = styled.span`
  color: #ef4444;
  margin-left: 2px;
`;

const ChangePreview = styled.div`
  padding: 12px 16px;
  background: #f0fdfb;
  border: 1px solid #99f6e4;
  border-radius: 10px;
`;

const ChangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ChangeFrom = styled.span`
  font-size: 13px;
  color: #94a3b8;
  text-decoration: line-through;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const ArrowIcon = styled.span`
  color: #00838c;
  font-size: 16px;
`;

const ChangeTo = styled.span`
  font-size: 13px;
  color: #0f766e;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 24px 24px;
`;
